import { ContactType } from "@tillix/db/enums";
import { computed } from "mobx";
import type { SnapshotOutOf } from "mobx-keystone";
import {
  _await,
  ExtendedModel,
  isoStringToDateTransform,
  Model,
  model,
  modelAction,
  modelFlow,
  prop,
  tProp,
  types,
} from "mobx-keystone";
import { api } from "../../../orpc/client";
import { GlobalTModel } from "../../lib/models/global-t-model";
import { TModel } from "../../lib/models/t-model";
import { nsModel } from "../../lib/namespace";
import { AddressModel } from "../address/model.address";
import type { ServiceAccountModel } from "../service-account/model.service-account";

const userNS = nsModel("user");

@model("tillix/UserPreferencesModel")
export class UserPreferencesModel extends Model({
  language: tProp(types.string, "en-US").withSetter(),
  communicationLanguage: tProp(types.string, "en-US").withSetter(),
  theme: tProp(
    types.enum({
      LIGHT: "LIGHT",
      DARK: "DARK",
      SYSTEM: "SYSTEM",
      TILLI: "TILLI",
    }),
    "LIGHT",
  ).withSetter(),
}) {
  @modelFlow
  *updateLanguage(language: string) {
    if (language === this.language) {
      return;
    }
    const _updated = yield* _await(
      api.user.preferences.put({
        language,
      }),
    );

    if (_updated.language !== this.language) {
      this.setLanguage(_updated.language);
    }
  }

  @modelFlow
  *updateCommunicationLanguage(communicationLanguage: string) {
    if (communicationLanguage === this.communicationLanguage) {
      return;
    }
    const _updated = yield* _await(
      api.user.preferences.put({
        communicationLanguage,
      }),
    );

    if (_updated.communicationLanguage !== this.communicationLanguage) {
      this.setCommunicationLanguage(_updated.communicationLanguage);
    }
  }

  @modelFlow
  *updateTheme(theme: "LIGHT" | "DARK" | "SYSTEM" | "TILLI") {
    if (theme === this.theme) {
      return;
    }
    const _updated = yield* _await(
      api.user.preferences.put({
        theme,
      }),
    );

    if (_updated.theme !== this.theme) {
      this.setTheme(_updated.theme);
    }
  }
}

@userNS.model
export default class UserModel extends ExtendedModel(GlobalTModel, {
  email: tProp(types.string).withSetter(),
  name: tProp(types.maybeNull(types.string)).withSetter(),
  profileImage: tProp(types.maybeNull(types.string)).withSetter(),
  phoneNumber: tProp(types.maybeNull(types.string)).withSetter(),
  primaryAddress: tProp(types.maybeNull(AddressModel)).withSetter(),
  createdAt: tProp(types.string).withTransform(isoStringToDateTransform()),
  updatedAt: tProp(types.string).withTransform(isoStringToDateTransform()),
  preferences: tProp(
    UserPreferencesModel,
    () => new UserPreferencesModel({}),
  ).withSetter(),
  // usersTenantRefIds: prop<Ref<TenantModel>[]>(() => []),
}) {
  protected omittedKeys: (keyof SnapshotOutOf<this>)[] = [];

  @modelFlow
  *update(this: UserModel, updates: Parameters<typeof api.user.put>[0]) {
    const _updated = yield* _await(
      api.user.put({
        ...updates,
      }),
    );

    if (_updated.name !== undefined && _updated.name !== this.name) {
      this.setName(_updated.name);
    }
    if (_updated.email !== undefined && _updated.email !== this.email) {
      this.setEmail(_updated.email);
    }
    if (
      _updated.phoneNumber !== undefined &&
      _updated.phoneNumber !== this.phoneNumber
    ) {
      this.setPhoneNumber(_updated.phoneNumber);
    }
    if (
      _updated.profileImage !== undefined &&
      _updated.profileImage !== this.profileImage
    ) {
      this.setProfileImage(_updated.profileImage);
    }

    if (_updated.primaryAddress !== undefined) {
      if (_updated.primaryAddress === null) {
        this.setPrimaryAddress(null);
      } else {
        this.setPrimaryAddress(
          new AddressModel({
            street1: _updated.primaryAddress.street1,
            street2: _updated.primaryAddress.street2 ?? null,
            city: _updated.primaryAddress.city,
            state: _updated.primaryAddress.state,
            country: _updated.primaryAddress.country,
            zip: _updated.primaryAddress.zip,
            countryCodeId: _updated.primaryAddress.countryCodeId ?? null,
          }),
        );
      }
    }
  }
}

@model("tillix/UserOnboardingModel")
export class UserOnboardingModel extends Model({
  complete: prop<boolean>(false),
  step: prop<string>("PROFILE"),
}) {
  @modelAction
  setStep(step: string) {
    this.step = step;
  }

  @modelAction
  setComplete(complete: boolean) {
    this.complete = complete;
  }
}

const userContactNS = nsModel("userContact");

@userContactNS.model
export class UserContactModel extends ExtendedModel(TModel, {
  type: tProp(types.enum(ContactType)),
  contactInfo: tProp(types.string),
  isPreferred: tProp(types.boolean, false),
  verified: tProp(types.boolean, false),
  serviceAccountId: tProp(types.maybeNull(types.string)),
}) {
  @computed
  get serviceAccount(): ServiceAccountModel | undefined {
    if (!this.detaching && this.root && this.serviceAccountId) {
      return this.root.tilliX.serviceAccounts.modelRef(this.serviceAccountId)
        .maybeCurrent;
    }
    // biome-ignore lint/nursery/noUselessUndefined: <return undefined for getter>
    return undefined;
  }
}
